import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Input, Label, Row } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { deleteApi, getApi, postApi } from "../../helpers/auth_helpers";
import ProcessFieldMappingEditor from "../../taxonomy/ProcessFieldMappingEditor";
import { getOrganizationTaxonomyConfig } from "../../taxonomy/taxonomyApi";
import type { ResolvedFieldConfig } from "../../taxonomy/types";

type ProcessMasterItem = {
  _id?: string;
  id?: string;
  name: string;
  description?: string;
  inhouse?: boolean;
  taxonomyIds?: string[];
};

const processMasterId = (item: ProcessMasterItem) => String(item._id || item.id || "").trim();

const ProcessMaster = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState<ProcessMasterItem[]>([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({
    id: "",
    name: "",
    description: "",
    inhouse: true,
    taxonomyIds: [] as string[],
  });
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [fieldsError, setFieldsError] = useState("");
  const [processFields, setProcessFields] = useState<ResolvedFieldConfig[]>([]);

  const loadData = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_SERVERURL}/product-process-master/list`;
      const response: any = await getApi(url);
      if (response?.success && Array.isArray(response?.data)) {
        setItems(
          response.data.map((item: ProcessMasterItem) => ({
            ...item,
            taxonomyIds: item.taxonomyIds || [],
          }))
        );
      } else {
        notifications({
          title: "Error!",
          message: response?.message || "Unable to fetch process master",
          type: "danger",
        });
      }
    } catch (error) {
      notifications({
        title: "Error!",
        message: "Unable to fetch process master",
        type: "danger",
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    getOrganizationTaxonomyConfig("PROCESS").then((res) => {
      if (res.success) setProcessFields(res.data || []);
      else setFieldsError("Custom fields could not be loaded. Existing field selections are preserved.");
      setFieldsLoading(false);
    });
  }, []);

  const resetForm = () => {
    setForm({ id: "", name: "", description: "", inhouse: true, taxonomyIds: [] });
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      notifications({
        title: "Error!",
        message: "Process name is required",
        type: "danger",
      });
      return;
    }
    setSaving(true);
    try {
      const url = `${process.env.REACT_APP_SERVERURL}/product-process-master/upsert`;
      const response: any = await postApi(url, {
        id: form.id || undefined,
        name: form.name.trim(),
        description: form.description.trim(),
        inhouse: !!form.inhouse,
      });
      if (response?.success) {
        notifications({
          title: "Success!",
          message: response?.message || "Process master saved",
          type: "success",
        });
        resetForm();
        loadData();
      } else {
        notifications({
          title: "Error!",
          message: response?.message || "Unable to save process master",
          type: "danger",
        });
      }
    } catch (error) {
      notifications({
        title: "Error!",
        message: "Unable to save process master",
        type: "danger",
      });
    }
    setSaving(false);
  };

  const handleSoftRemove = async (id: string) => {
    const allow = window.confirm("Soft remove this process?");
    if (!allow) return;
    try {
      const url = `${process.env.REACT_APP_SERVERURL}/product-process-master/remove?id=${id}`;
      const response: any = await deleteApi(url);
      if (response?.success) {
        notifications({
          title: "Success!",
          message: response?.message || "Process removed",
          type: "success",
        });
        if (form.id === id) resetForm();
        loadData();
      } else {
        notifications({
          title: "Error!",
          message: response?.message || "Unable to remove process",
          type: "danger",
        });
      }
    } catch (error) {
      notifications({
        title: "Error!",
        message: "Unable to remove process",
        type: "danger",
      });
    }
  };

  const visibleItems = items.filter((item) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      String(item?.name || "").toLowerCase().includes(q) ||
      String(item?.description || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="userMainDiv">
      <Card className="createProductCard p-3">
        <h4 className="mb-2">Process Master</h4>
        <p className="text-muted mb-4">
          Organization-wise process names for product upload steps.
        </p>
        <CardBody className="p-0">
          <Row className="g-3 align-items-end mb-3">
            <Col xs={12} md={4} lg={3}>
              <Label>Process name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Dyeing"
              />
            </Col>
            <Col xs={12} md={5} lg={5}>
              <Label>Subtitle / description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Short helper text"
              />
            </Col>
            <Col xs={12} md={3} lg={2}>
              <Label className="d-block">Source</Label>
              <div className="d-flex align-items-center gap-2" style={{ minHeight: 38 }}>
                <Input
                  type="checkbox"
                  checked={!!form.inhouse}
                  onChange={(e) => setForm({ ...form, inhouse: e.target.checked })}
                />
                <span>{form.inhouse ? "Inhouse" : "Outsourced"}</span>
              </div>
            </Col>
            <Col xs={12}>
              {fieldsLoading ? <p role="status">Loading custom fields…</p> : null}
              {fieldsError ? <p role="alert">{fieldsError}</p> : null}
              <ProcessFieldMappingEditor
                processMasterId={form.id || undefined}
                fields={processFields}
                onChange={loadData}
              />
            </Col>
            <Col xs={12} md={12} lg={2} className="d-flex justify-content-lg-end gap-2">
              {form.id ? (
                <Button color="secondary" outline size="sm" style={{ minWidth: 96 }} onClick={resetForm}>
                  Cancel edit
                </Button>
              ) : null}
              <Button
                color="primary"
                size="sm"
                style={{ minWidth: 96 }}
                onClick={handleSave}
                disabled={saving}
              >
                {form.id ? "Update" : "Add"}
              </Button>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col xs={12} md={6} lg={4}>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search process master"
              />
            </Col>
          </Row>

          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th style={{ width: "35%" }}>Name</th>
                  <th>Description</th>
                  <th style={{ width: "140px" }}>Source</th>
                  <th style={{ width: "180px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={4} className="text-center">
                      Loading...
                    </td>
                  </tr>
                ) : visibleItems.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center">
                      No process master found.
                    </td>
                  </tr>
                ) : (
                  visibleItems.map((item) => (
                    <tr key={processMasterId(item) || item.name}>
                      <td>{item.name}</td>
                      <td>{item.description || "-"}</td>
                      <td>{item.inhouse === false ? "Outsourced" : "Inhouse"}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <Button
                            size="sm"
                            color="secondary"
                            outline
                            onClick={() =>
                              setForm({
                                id: processMasterId(item),
                                name: item.name || "",
                                description: item.description || "",
                                inhouse: item.inhouse === false ? false : true,
                                taxonomyIds: item.taxonomyIds || [],
                              })
                            }
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            color="danger"
                            outline
                            onClick={() => handleSoftRemove(processMasterId(item))}
                          >
                            Remove
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default ProcessMaster;
